import { useMemo, useState } from 'react';
import { Plus, Search, Globe } from 'lucide-react';
import { Toolbar } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import {
  Button,
  Input,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { Field } from '@vnetwork/ui/forms';
import { CountFooter, ListSkeleton } from './GenericList';
import {
  useElasticIps,
  useAllocateElasticIp,
  useAttachElasticIp,
  useDetachElasticIp,
  useReleaseElasticIp,
  useInstanceList,
  type ElasticIp,
} from '@/api/opennebula';
import { RowMenu } from '../components/CrudBits';
import { LargeOptionPicker } from '../components/LargeOptionPicker';

export function ElasticIps({ serviceId }: { serviceId: string }) {
  const { show } = useToast();
  const { data: ips = [], isLoading } = useElasticIps(serviceId);
  const [release, setRelease] = useState<ElasticIp | null>(null);
  const [attachTarget, setAttachTarget] = useState<ElasticIp | null>(null);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ips;
    return ips.filter((i) =>
      [i.ip, i.ip_address, i.id, i.instance_name, i.instance_id, i.use_type].some((f) => String(f ?? '').toLowerCase().includes(q)),
    );
  }, [ips, query]);

  const allocate = useAllocateElasticIp(serviceId, { onSuccess: () => show({ tone: 'success', title: 'Elastic IP allocated' }), onError: () => show({ tone: 'danger', title: 'Allocate failed' }) });
  const detach = useDetachElasticIp(serviceId, { onSuccess: () => show({ tone: 'success', title: 'Detached' }), onError: () => show({ tone: 'danger', title: 'Detach failed' }) });
  const rel = useReleaseElasticIp(serviceId, { onSuccess: () => { show({ tone: 'success', title: 'Released' }); setRelease(null); }, onError: () => show({ tone: 'danger', title: 'Release failed' }) });

  const columns: ColumnDef<ElasticIp, unknown>[] = [
    { id: 'ip', header: 'IP address', cell: ({ row }) => <b style={{ fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>{row.original.ip ?? row.original.ip_address ?? row.original.id}</b> },
    { id: 'instance', header: 'Attached to', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{row.original.instance_name || row.original.instance_id || '—'}</span> },
    { id: 'use_type', header: 'Type', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)', textTransform: 'capitalize' }}>{row.original.use_type || '—'}</span> },
    { id: 'state', header: 'State', cell: ({ row }) => <StatusPill tone={row.original.instance_id ? 'success' : 'muted'}>{row.original.instance_id ? 'In use' : 'Available'}</StatusPill> },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <RowMenu
          items={[
            ...(row.original.instance_id
              ? [{ label: 'Detach', onSelect: () => detach.mutate(row.original.id) }]
              : [{ label: 'Attach to instance', onSelect: () => setAttachTarget(row.original) }]),
            { label: 'Release', danger: true, onSelect: () => setRelease(row.original) },
          ]}
        />
      ),
    },
  ];

  return (
    <>
      <Toolbar
        start={<Input placeholder="Search elastic IPs" startSlot={<Search />} value={query} onChange={(e) => setQuery(e.target.value)} style={{ maxWidth: 360 }} />}
        end={<Button startIcon={<Plus size={15} />} disabled={allocate.isPending} onClick={() => allocate.mutate()}>Allocate IP</Button>}
      />
      {isLoading ? <ListSkeleton /> : (
        <DataTable
          columns={columns}
          data={filtered}
          getRowId={(i) => String(i.id)}
          footer={filtered.length ? <CountFooter n={filtered.length} noun="Elastic IPs" /> : undefined}
          emptyState={
            ips.length === 0 ? (
              <EmptyState
                icon={<Globe size={28} />}
                title="No elastic IPs yet"
                description="Allocate a floating public IP to attach to an instance."
                actions={<Button startIcon={<Plus size={15} />} disabled={allocate.isPending} onClick={() => allocate.mutate()}>Allocate IP</Button>}
              />
            ) : (
              'No elastic IPs match your search.'
            )
          }
        />
      )}
      <ConfirmDialog open={!!release} onOpenChange={(o) => !o && setRelease(null)} title="Release elastic IP?" description={`Release ${release?.ip ?? release?.ip_address ?? release?.id}? It returns to the pool.`} confirmLabel="Release" destructive loading={rel.isPending} onConfirm={() => release && rel.mutate(release.id)} />
      {attachTarget && <AttachElasticIpDialog serviceId={serviceId} eip={attachTarget} onClose={() => setAttachTarget(null)} />}
    </>
  );
}

/** Attach an available elastic IP to an instance as a NIC alias or a floating
 *  (1:1 NAT) IP — mirrors the legacy attach dialog. */
function AttachElasticIpDialog({ serviceId, eip, onClose }: { serviceId: string; eip: ElasticIp; onClose: () => void }) {
  const { show } = useToast();
  const { data: instances = [], isLoading } = useInstanceList(serviceId);
  const available = instances.filter((i) => i.status !== 'TERMINATED');
  const [useType, setUseType] = useState<'alias' | 'floating'>('alias');
  const [instanceId, setInstanceId] = useState('');

  const attach = useAttachElasticIp(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'Elastic IP attached' }); onClose(); },
    onError: () => show({ tone: 'danger', title: 'Attach failed' }),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="md">
        <DialogHeader><DialogTitle>Attach {eip.ip ?? eip.ip_address ?? eip.id}</DialogTitle></DialogHeader>
        <DialogBody>
          <p style={{ margin: '0 0 var(--vn-space-3)', fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
            Attach <code>{eip.ip ?? eip.ip_address ?? eip.id}</code> to an active instance and choose how it is attached.
          </p>
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <Field label="Attachment type">
              {() => (
                <LargeOptionPicker
                  columns={2}
                  value={useType}
                  onChange={(v) => setUseType(v as 'alias' | 'floating')}
                  options={[
                    { value: 'alias', title: 'Alias IP', subtitle: "Direct NIC alias — IP appears on the instance's network interface." },
                    { value: 'floating', title: 'Floating IP', subtitle: 'NAT 1:1 mapping — traffic routed through the router.' },
                  ]}
                />
              )}
            </Field>
            <Field label="Instance">
              {() => (
                isLoading ? <span /> : available.length === 0 ? (
                  <p style={{ margin: 0, color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>No instances available to attach to.</p>
                ) : (
                  <Select value={instanceId} onValueChange={setInstanceId}>
                    <SelectTrigger style={{ width: '100%' }}><SelectValue placeholder="Select an instance" /></SelectTrigger>
                    <SelectContent>
                      {available.map((i) => <SelectItem key={i.id} value={i.id}>{i.name || i.id}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )
              )}
            </Field>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button disabled={!instanceId || attach.isPending} onClick={() => attach.mutate({ id: eip.id, instance_id: instanceId, use_type: useType })}>Attach</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
